import { useState, useEffect, useCallback } from "react";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");

const api = async (path, opts = {}) => {
  const res = await fetch(API + path, {
    ...opts,
    headers: { "Content-Type":"application/json", ...(getToken()?{Authorization:"Bearer "+getToken()}:{}), ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const INR = (n) => "₹" + Number(n||0).toLocaleString("en-IN");

const PLANS = {
  trial:      { label:"Free Trial",  color:"#6b7280", bg:"#f3f4f6" },
  starter:    { label:"Starter",     color:"#2563eb", bg:"#eff6ff" },
  growth:     { label:"Growth",      color:"#F47920", bg:"#fff3ea" },
  enterprise: { label:"Enterprise",  color:"#7c3aed", bg:"#f5f3ff" },
};

const PLATFORMS = [
  { id:"shopify",  name:"Shopify",  color:"#5a8a00" },
  { id:"amazon",   name:"Amazon",   color:"#c45000" },
  { id:"flipkart", name:"Flipkart", color:"#1b57c0" },
  { id:"myntra",   name:"Myntra",   color:"#b5003c" },
  { id:"meesho",   name:"Meesho",   color:"#9f2089" },
];

const S = {
  card:    { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 },
  cardHdr: { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", fontWeight:600, fontSize:13 },
  btn:     (c="#F47920") => ({ padding:"8px 18px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut:  { padding:"7px 14px", borderRadius:7, fontSize:12, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  input:   { width:"100%", padding:"8px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:13, fontFamily:"inherit", outline:"none", marginBottom:10 },
  label:   { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
  tab:     (a) => ({ padding:"10px 20px", background:"none", border:"none", borderBottom:a?"2px solid #F47920":"2px solid transparent", color:a?"#F47920":"#6b7280", fontWeight:a?600:400, fontSize:13, cursor:"pointer", fontFamily:"inherit" }),
};

const Badge = ({ label, color, bg }) => (
  <span style={{ background:bg, color, padding:"3px 10px", borderRadius:20, fontSize:12, fontWeight:600 }}>{label}</span>
);

function ProgressBar({ value, max, color="#F47920" }) {
  const pct = max > 0 ? Math.min(100, (value/max)*100) : 0;
  return (
    <div style={{ height:6, background:"#f3f4f6", borderRadius:3, overflow:"hidden" }}>
      <div style={{ height:"100%", width:pct+"%", background:color, borderRadius:3, transition:"width .5s" }}/>
    </div>
  );
}

export default function PageProfile({ toast, tenant, onTenantUpdate, onLogout }) {
  const [tab,      setTab]      = useState("profile");
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [copyDone, setCopyDone] = useState("");

  // profile form
  const [profile, setProfile] = useState({
    storeName:  tenant?.storeName  || "",
    phone:      tenant?.phone      || "",
    gstin:      tenant?.gstin      || "",
    pan:        tenant?.pan        || "",
    hsn:        tenant?.hsn        || "",
    address:    tenant?.address    || "",
    city:       tenant?.city       || "",
    state:      tenant?.state      || "",
    pincode:    tenant?.pincode    || "",
    bankName:   tenant?.bankName   || "",
    accountNo:  tenant?.accountNo  || "",
    ifsc:       tenant?.ifsc       || "",
    upiId:      tenant?.upiId      || "",
    website:    tenant?.website    || "",
    description:tenant?.description|| "",
  });

  // password form
  const [pwForm, setPwForm] = useState({ current:"", newPw:"", confirm:"" });
  const [pwError, setPwError] = useState("");

  // notifications
  const [notif, setNotif] = useState({
    emailOrders:    tenant?.notifications?.emailOrders    !== false,
    emailLowStock:  tenant?.notifications?.emailLowStock  !== false,
    emailReturns:   tenant?.notifications?.emailReturns   !== false,
    whatsappOrders: tenant?.notifications?.whatsappOrders || false,
    whatsappAlerts: tenant?.notifications?.whatsappAlerts || false,
  });

  const plan      = PLANS[tenant?.plan] || PLANS.trial;
  const trialDays = tenant?.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(tenant.trialEndsAt) - Date.now()) / 86400000))
    : 0;
  const memberSince = tenant?.createdAt
    ? new Date(tenant.createdAt).toLocaleDateString("en-IN", { month:"long", year:"numeric" })
    : "—";

  const limits = tenant?.limits || { platforms:2, skus:500, orders:500 };

  // copy to clipboard
  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopyDone(label);
    toast(label + " copied ✓", "success");
    setTimeout(() => setCopyDone(""), 2000);
  };

  // save profile
  const saveProfile = async () => {
    setSaving(true);
    try {
      const data = await api("/auth/profile", {
        method: "PUT",
        body: JSON.stringify(profile),
      });
      toast("Profile updated ✓", "success");
      if (onTenantUpdate) onTenantUpdate(data.tenant);
    } catch(e) { toast(e.message, "error"); }
    finally { setSaving(false); }
  };

  // change password
  const changePassword = async () => {
    setPwError("");
    if (!pwForm.current)            { setPwError("Enter current password"); return; }
    if (pwForm.newPw.length < 8)    { setPwError("New password must be 8+ characters"); return; }
    if (pwForm.newPw !== pwForm.confirm) { setPwError("Passwords don't match"); return; }
    setSaving(true);
    try {
      await api("/auth/change-password", {
        method: "PUT",
        body: JSON.stringify({ currentPassword: pwForm.current, newPassword: pwForm.newPw }),
      });
      toast("Password changed ✓", "success");
      setPwForm({ current:"", newPw:"", confirm:"" });
    } catch(e) { setPwError(e.message); }
    finally { setSaving(false); }
  };

  // save notifications
  const saveNotifications = async () => {
    setSaving(true);
    try {
      await api("/auth/profile", { method:"PUT", body:JSON.stringify({ notifications:notif }) });
      toast("Notification preferences saved ✓", "success");
    } catch(e) { toast(e.message, "error"); }
    finally { setSaving(false); }
  };

  // delete account
  const deleteAccount = async () => {
    if (!window.confirm("Are you sure? This will permanently delete your account and all data. This cannot be undone.")) return;
    const confirm2 = window.prompt("Type DELETE to confirm:");
    if (confirm2 !== "DELETE") { toast("Deletion cancelled", "info"); return; }
    try {
      await api("/auth/account", { method:"DELETE" });
      toast("Account deleted", "success");
      setTimeout(() => { localStorage.clear(); window.location.reload(); }, 1500);
    } catch(e) { toast(e.message, "error"); }
  };

  return (
    <div style={{ maxWidth:860, margin:"0 auto" }}>

      {/* ── PROFILE HEADER CARD ── */}
      <div style={{ ...S.card, padding:0, overflow:"hidden", marginBottom:20 }}>
        {/* orange banner */}
        <div style={{ height:80, background:`linear-gradient(135deg, #F47920 0%, #C45E0A 100%)` }}/>
        <div style={{ padding:"0 24px 24px", marginTop:-40 }}>
          <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
            <div style={{ display:"flex", alignItems:"flex-end", gap:16 }}>
              {/* avatar */}
              <div style={{ width:80, height:80, borderRadius:"50%", background:"#fff", border:"4px solid #fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:32, color:"#F47920", boxShadow:"0 4px 12px rgba(0,0,0,0.12)", flexShrink:0 }}>
                {tenant?.storeName?.charAt(0)?.toUpperCase() || "S"}
              </div>
              <div style={{ paddingBottom:4 }}>
                <div style={{ fontWeight:800, fontSize:20, color:"#111" }}>{tenant?.storeName}</div>
                <div style={{ fontSize:13, color:"#6b7280", display:"flex", gap:10, flexWrap:"wrap", marginTop:4 }}>
                  {tenant?.username && <span style={{ fontFamily:"monospace" }}>@{tenant.username}</span>}
                  {tenant?.sellerId && (
                    <span style={{ display:"flex", alignItems:"center", gap:4 }}>
                      🆔 {tenant.sellerId}
                      <button onClick={() => copy(tenant.sellerId, "Seller ID")} style={{ background:"none", border:"none", cursor:"pointer", fontSize:12, color:"#9ca3af" }}>📋</button>
                    </span>
                  )}
                  <span>📅 Member since {memberSince}</span>
                </div>
              </div>
            </div>
            <div style={{ display:"flex", gap:8, alignItems:"center", paddingBottom:4 }}>
              <Badge label={plan.label} color={plan.color} bg={plan.bg}/>
              {tenant?.plan === "trial" && <span style={{ fontSize:12, color:"#d97706" }}>⏳ {trialDays} days left</span>}
            </div>
          </div>

          {/* quick stats */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginTop:20 }}>
            {[
              { label:"Email",    value:tenant?.email,                                    icon:"✉️" },
              { label:"Plan",     value:plan.label,                                       icon:"⭐" },
              { label:"Seller ID",value:tenant?.sellerId || "Generating…",               icon:"🆔" },
              { label:"Username", value:"@" + (tenant?.username || "—"),                 icon:"👤" },
              { label:"Status",   value:tenant?.isActive ? "Active" : "Suspended",       icon:"✅" },
            ].map((item) => (
              <div key={item.label} style={{ background:"#f9fafb", borderRadius:8, padding:"10px 12px" }}>
                <div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>{item.icon} {item.label}</div>
                <div style={{ fontWeight:600, fontSize:12, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── TABS ── */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:16, flexWrap:"wrap" }}>
        {[
          { id:"profile",  label:"👤 Profile"         },
          { id:"business", label:"🏪 Business"        },
          { id:"usage",    label:"📊 Usage & Plan"    },
          { id:"security", label:"🔐 Security"        },
          { id:"notif",    label:"🔔 Notifications"   },
          { id:"danger",   label:"⚠️ Danger Zone"     },
        ].map((t) => (
          <button key={t.id} style={S.tab(tab===t.id)} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* ── PROFILE TAB ── */}
      {tab === "profile" && (
        <div style={S.card}>
          <div style={S.cardHdr}><span>Personal & Store Details</span></div>
          <div style={{ padding:20 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <div>
                <label style={S.label}>Store / Brand Name</label>
                <input style={S.input} value={profile.storeName}
                  onChange={(e) => setProfile((f) => ({ ...f, storeName:e.target.value }))}/>
              </div>
              <div>
                <label style={S.label}>Phone</label>
                <input style={S.input} placeholder="+91 98XXXXXXXX" value={profile.phone}
                  onChange={(e) => setProfile((f) => ({ ...f, phone:e.target.value }))}/>
              </div>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={S.label}>Website</label>
                <input style={S.input} placeholder="https://yourstore.com" value={profile.website}
                  onChange={(e) => setProfile((f) => ({ ...f, website:e.target.value }))}/>
              </div>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={S.label}>Store Description</label>
                <textarea style={{ ...S.input, height:70, resize:"vertical" }}
                  placeholder="Brief description of your store and products…"
                  value={profile.description}
                  onChange={(e) => setProfile((f) => ({ ...f, description:e.target.value }))}/>
              </div>
            </div>
            <div style={{ marginTop:8 }}>
              <label style={S.label}>Email</label>
              <input style={{ ...S.input, background:"#f9fafb", color:"#9ca3af" }} value={tenant?.email} disabled/>
              <div style={{ fontSize:11, color:"#9ca3af" }}>Email cannot be changed. Contact support if needed.</div>
            </div>
            <button style={{ ...S.btn(), marginTop:16, opacity:saving?0.7:1 }} onClick={saveProfile} disabled={saving}>
              {saving ? "Saving…" : "Save Profile"}
            </button>
          </div>
        </div>
      )}

      {/* ── BUSINESS TAB ── */}
      {tab === "business" && (
        <>
          <div style={S.card}>
            <div style={S.cardHdr}><span>GST & Tax Details</span></div>
            <div style={{ padding:20 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14 }}>
                <div>
                  <label style={S.label}>GSTIN</label>
                  <input style={S.input} placeholder="09BDOPB0236M2ZF" value={profile.gstin}
                    onChange={(e) => setProfile((f) => ({ ...f, gstin:e.target.value.toUpperCase() }))}/>
                </div>
                <div>
                  <label style={S.label}>PAN</label>
                  <input style={S.input} placeholder="BDOPB0236M" value={profile.pan}
                    onChange={(e) => setProfile((f) => ({ ...f, pan:e.target.value.toUpperCase() }))}/>
                </div>
                <div>
                  <label style={S.label}>Default HSN Code</label>
                  <input style={S.input} placeholder="42033000" value={profile.hsn}
                    onChange={(e) => setProfile((f) => ({ ...f, hsn:e.target.value }))}/>
                </div>
              </div>
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardHdr}><span>Business Address</span></div>
            <div style={{ padding:20 }}>
              <label style={S.label}>Street Address</label>
              <input style={S.input} placeholder="Tower 4C, Landcraft Golflinks" value={profile.address}
                onChange={(e) => setProfile((f) => ({ ...f, address:e.target.value }))}/>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14 }}>
                <div>
                  <label style={S.label}>City</label>
                  <input style={S.input} value={profile.city} onChange={(e) => setProfile((f) => ({ ...f, city:e.target.value }))}/>
                </div>
                <div>
                  <label style={S.label}>State</label>
                  <input style={S.input} value={profile.state} onChange={(e) => setProfile((f) => ({ ...f, state:e.target.value }))}/>
                </div>
                <div>
                  <label style={S.label}>Pincode</label>
                  <input style={S.input} value={profile.pincode} onChange={(e) => setProfile((f) => ({ ...f, pincode:e.target.value }))}/>
                </div>
              </div>
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardHdr}><span>Bank & Payment Details</span></div>
            <div style={{ padding:20 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                <div>
                  <label style={S.label}>Bank Name</label>
                  <input style={S.input} placeholder="HDFC Bank" value={profile.bankName}
                    onChange={(e) => setProfile((f) => ({ ...f, bankName:e.target.value }))}/>
                </div>
                <div>
                  <label style={S.label}>Account Number</label>
                  <input style={S.input} placeholder="50XXXXXXXXXX" value={profile.accountNo}
                    onChange={(e) => setProfile((f) => ({ ...f, accountNo:e.target.value }))}/>
                </div>
                <div>
                  <label style={S.label}>IFSC Code</label>
                  <input style={S.input} placeholder="HDFC0001234" value={profile.ifsc}
                    onChange={(e) => setProfile((f) => ({ ...f, ifsc:e.target.value.toUpperCase() }))}/>
                </div>
                <div>
                  <label style={S.label}>UPI ID</label>
                  <input style={S.input} placeholder="seller@upi" value={profile.upiId}
                    onChange={(e) => setProfile((f) => ({ ...f, upiId:e.target.value }))}/>
                </div>
              </div>
              <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>These details appear on your PDF invoices automatically.</div>
            </div>
          </div>

          <button style={{ ...S.btn(), opacity:saving?0.7:1 }} onClick={saveProfile} disabled={saving}>
            {saving ? "Saving…" : "Save Business Details"}
          </button>
        </>
      )}

      {/* ── USAGE & PLAN TAB ── */}
      {tab === "usage" && (
        <>
          {/* plan card */}
          <div style={{ ...S.card, borderTop:`4px solid ${plan.color}` }}>
            <div style={{ padding:20 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:18 }}>{plan.label} Plan</div>
                  <div style={{ fontSize:13, color:"#6b7280" }}>
                    {tenant?.plan === "trial"
                      ? `${trialDays} days remaining in free trial`
                      : tenant?.planExpiresAt
                        ? `Renews ${new Date(tenant.planExpiresAt).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"})}`
                        : "Active subscription"}
                  </div>
                </div>
                <Badge label={plan.label} color={plan.color} bg={plan.bg}/>
              </div>

              {/* seller ID card */}
              <div style={{ background:"#f9fafb", borderRadius:10, padding:16, marginBottom:16, border:"1px solid #e5e7eb" }}>
                <div style={{ fontSize:11, color:"#9ca3af", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.5px" }}>Your 3June Identity</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  {[
                    { label:"Seller ID",    value:tenant?.sellerId || "SS-00000",   icon:"🆔" },
                    { label:"Username",     value:"@"+(tenant?.username||"seller"),  icon:"👤" },
                    { label:"Account Type", value:plan.label,                        icon:"⭐" },
                    { label:"Member Since", value:memberSince,                       icon:"📅" },
                  ].map((item) => (
                    <div key={item.label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div>
                        <div style={{ fontSize:10, color:"#9ca3af" }}>{item.icon} {item.label}</div>
                        <div style={{ fontWeight:600, fontSize:13, fontFamily:item.label.includes("ID")||item.label.includes("Username")?"monospace":"inherit" }}>{item.value}</div>
                      </div>
                      <button onClick={() => copy(item.value.replace("@",""), item.label)}
                        style={{ background:"none", border:"none", cursor:"pointer", fontSize:14, color:"#9ca3af" }}>
                        {copyDone === item.label ? "✓" : "📋"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* usage limits */}
              <div style={{ fontWeight:600, fontSize:13, marginBottom:12 }}>Usage This Month</div>
              {[
                { label:"Platforms Connected", used:PLATFORMS.filter((p) => tenant?.platforms?.[p.id]?.connected).length, max:limits.platforms, color:"#2563eb" },
                { label:"Products / SKUs",      used:0,   max:limits.skus,    color:"#F47920", note:"Sync to see count" },
                { label:"Orders This Month",    used:0,   max:limits.orders,  color:"#15803d", note:"Sync to see count" },
              ].map((item) => (
                <div key={item.label} style={{ marginBottom:14 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:6 }}>
                    <span style={{ fontWeight:500 }}>{item.label}</span>
                    <span style={{ color:item.color, fontWeight:600 }}>
                      {item.used} / {item.max === 99999 ? "Unlimited" : item.max.toLocaleString()}
                      {item.note && <span style={{ color:"#9ca3af", fontWeight:400, marginLeft:6, fontSize:11 }}>({item.note})</span>}
                    </span>
                  </div>
                  <ProgressBar value={item.used} max={item.max === 99999 ? item.used+1 : item.max} color={item.color}/>
                </div>
              ))}

              {/* platform status */}
              <div style={{ fontWeight:600, fontSize:13, margin:"16px 0 10px" }}>Platform Connections</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:8 }}>
                {PLATFORMS.map((pl) => {
                  const connected = tenant?.platforms?.[pl.id]?.connected;
                  return (
                    <div key={pl.id} style={{ textAlign:"center", padding:"10px 6px", borderRadius:8, border:`1px solid ${connected?pl.color+"40":"#e5e7eb"}`, background:connected?pl.color+"08":"#f9fafb" }}>
                      <div style={{ fontSize:11, fontWeight:700, color:connected?pl.color:"#9ca3af" }}>{pl.name}</div>
                      <div style={{ fontSize:10, marginTop:4, color:connected?"#15803d":"#9ca3af" }}>{connected?"✓ Live":"Not set"}</div>
                    </div>
                  );
                })}
              </div>

              {tenant?.plan !== "enterprise" && (
                <div style={{ background:"#fff3ea", border:"1px solid #fed7aa", borderRadius:8, padding:12, marginTop:16, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ fontSize:13, color:"#92400e" }}>Upgrade for more platforms, SKUs and team members</div>
                  <button style={S.btn()} onClick={() => toast("Go to Billing & Plans to upgrade!", "info")}>⚡ Upgrade</button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── SECURITY TAB ── */}
      {tab === "security" && (
        <>
          <div style={S.card}>
            <div style={S.cardHdr}><span>Change Password</span></div>
            <div style={{ padding:20, maxWidth:480 }}>
              {pwError && (
                <div style={{ background:"#fef2f2", color:"#b91c1c", padding:"10px 14px", borderRadius:8, fontSize:13, marginBottom:14, border:"1px solid #fecaca" }}>{pwError}</div>
              )}
              <label style={S.label}>Current Password</label>
              <input style={S.input} type="password" placeholder="••••••••" value={pwForm.current}
                onChange={(e) => setPwForm((f) => ({ ...f, current:e.target.value }))}/>
              <label style={S.label}>New Password</label>
              <input style={S.input} type="password" placeholder="Min 8 characters" value={pwForm.newPw}
                onChange={(e) => setPwForm((f) => ({ ...f, newPw:e.target.value }))}/>
              <label style={S.label}>Confirm New Password</label>
              <input style={S.input} type="password" placeholder="Repeat new password" value={pwForm.confirm}
                onChange={(e) => setPwForm((f) => ({ ...f, confirm:e.target.value }))}/>
              <button style={{ ...S.btn(), opacity:saving?0.7:1 }} onClick={changePassword} disabled={saving}>
                {saving ? "Changing…" : "Change Password"}
              </button>
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardHdr}><span>Login Sessions</span></div>
            <div style={{ padding:20 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", background:"#f0fdf4", borderRadius:8, border:"1px solid #bbf7d0" }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:13 }}>Current Session</div>
                  <div style={{ fontSize:12, color:"#6b7280" }}>
                    Last login: {tenant?.lastLogin ? new Date(tenant.lastLogin).toLocaleString("en-IN") : "Now"}
                  </div>
                </div>
                <Badge label="Active" color="#15803d" bg="#f0fdf4"/>
              </div>
              <div style={{ marginTop:12 }}>
                <button style={{ ...S.btnOut, color:"#dc2626", borderColor:"#fecaca" }} onClick={() => { localStorage.clear(); window.location.reload(); }}>
                  Log out all devices
                </button>
              </div>
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardHdr}><span>Account Info</span></div>
            <div style={{ padding:20 }}>
              {[
                ["Seller ID",       tenant?.sellerId     || "—"],
                ["Username",        "@"+(tenant?.username|| "—")],
                ["Email",           tenant?.email        || "—"],
                ["Google Account",  tenant?.googleId ? "Connected ✓" : "Not connected"],
                ["Email Verified",  tenant?.isEmailVerified ? "Yes ✓" : "No"],
                ["Account Created", tenant?.createdAt ? new Date(tenant.createdAt).toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"}) : "—"],
              ].map(([label, value]) => (
                <div key={label} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid #f3f4f6", fontSize:13 }}>
                  <span style={{ color:"#6b7280" }}>{label}</span>
                  <span style={{ fontWeight:500, fontFamily:label.includes("ID")||label.includes("Username")?"monospace":"inherit" }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── NOTIFICATIONS TAB ── */}
      {tab === "notif" && (
        <div style={S.card}>
          <div style={S.cardHdr}><span>Notification Preferences</span></div>
          <div style={{ padding:20 }}>
            <div style={{ fontWeight:600, fontSize:13, marginBottom:12 }}>📧 Email Notifications</div>
            {[
              { key:"emailOrders",   label:"New order received",        desc:"Get email when a new order arrives" },
              { key:"emailLowStock", label:"Low stock alert",           desc:"When product stock falls below 10 units" },
              { key:"emailReturns",  label:"Return initiated",          desc:"When a customer initiates a return" },
            ].map((item) => (
              <div key={item.key} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 0", borderBottom:"1px solid #f3f4f6" }}>
                <div>
                  <div style={{ fontWeight:500, fontSize:13 }}>{item.label}</div>
                  <div style={{ fontSize:12, color:"#9ca3af" }}>{item.desc}</div>
                </div>
                <button onClick={() => setNotif((f) => ({ ...f, [item.key]:!f[item.key] }))}
                  style={{ width:44, height:24, borderRadius:12, background:notif[item.key]?"#22c55e":"#d1d5db", position:"relative", cursor:"pointer", border:"none", transition:"background .2s", flexShrink:0 }}>
                  <div style={{ width:20, height:20, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:notif[item.key]?22:2, transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,0.2)" }}/>
                </button>
              </div>
            ))}

            <div style={{ fontWeight:600, fontSize:13, margin:"20px 0 12px" }}>💬 WhatsApp Notifications</div>
            {[
              { key:"whatsappOrders", label:"WhatsApp order alerts",   desc:"Receive WhatsApp when new orders arrive" },
              { key:"whatsappAlerts", label:"WhatsApp stock alerts",   desc:"Receive WhatsApp for low stock warnings" },
            ].map((item) => (
              <div key={item.key} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 0", borderBottom:"1px solid #f3f4f6" }}>
                <div>
                  <div style={{ fontWeight:500, fontSize:13 }}>{item.label}</div>
                  <div style={{ fontSize:12, color:"#9ca3af" }}>{item.desc}</div>
                </div>
                <button onClick={() => setNotif((f) => ({ ...f, [item.key]:!f[item.key] }))}
                  style={{ width:44, height:24, borderRadius:12, background:notif[item.key]?"#25D366":"#d1d5db", position:"relative", cursor:"pointer", border:"none", transition:"background .2s", flexShrink:0 }}>
                  <div style={{ width:20, height:20, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:notif[item.key]?22:2, transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,0.2)" }}/>
                </button>
              </div>
            ))}

            <button style={{ ...S.btn(), marginTop:20, opacity:saving?0.7:1 }} onClick={saveNotifications} disabled={saving}>
              {saving ? "Saving…" : "Save Preferences"}
            </button>
          </div>
        </div>
      )}

      {/* ── DANGER ZONE TAB ── */}
      {tab === "danger" && (
        <>
          <div style={{ ...S.card, border:"1px solid #fecaca" }}>
            <div style={{ ...S.cardHdr, borderBottom:"1px solid #fecaca", color:"#dc2626" }}>⚠️ Danger Zone</div>
            <div style={{ padding:20 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"16px 0", borderBottom:"1px solid #f3f4f6" }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:14 }}>Log out</div>
                  <div style={{ fontSize:12, color:"#6b7280" }}>Sign out of your current session</div>
                </div>
                <button style={{ ...S.btnOut }} onClick={onLogout}>Log out</button>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"16px 0", borderBottom:"1px solid #f3f4f6" }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:14 }}>Export all data</div>
                  <div style={{ fontSize:12, color:"#6b7280" }}>Download a copy of all your 3June data</div>
                </div>
                <button style={{ ...S.btnOut }} onClick={() => toast("Data export coming soon", "info")}>Export Data</button>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"16px 0" }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:14, color:"#dc2626" }}>Delete Account</div>
                  <div style={{ fontSize:12, color:"#6b7280" }}>Permanently delete your account and all data. This cannot be undone.</div>
                </div>
                <button style={{ ...S.btn("#dc2626") }} onClick={deleteAccount}>Delete Account</button>
              </div>
            </div>
          </div>

          <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:14, fontSize:13, color:"#92400e" }}>
            ⚠️ Before deleting: Cancel your Razorpay subscription first to avoid future charges. Contact support at leatherpatta@gmail.com if you need help exporting your data.
          </div>
        </>
      )}
    </div>
  );
}
